import {
  BriefcaseIcon,
  ContactIcon,
  DumbbellIcon,
  GraduationCapIcon,
  HistoryIcon,
  PaletteIcon,
  PersonStandingIcon,
  RepeatIcon,
  ScissorsIcon,
  SmartphoneIcon,
  SparklesIcon,
  UserPlusIcon,
  type LucideIcon,
} from "lucide-react";

/**
 * Everything the landing page says, in one place.
 *
 * Plan limits and prices mirror MainApp's billing catalogue
 * (`PocketBase/MainApp/billing/catalogue.go`). If a plan changes there, it
 * changes here — this page must never promise more than the product enforces.
 */

export type NavLink = { label: string; id: string };

export const landingNavLinks: NavLink[] = [
  { label: "Product", id: "product" },
  { label: "How it works", id: "how-it-works" },
  { label: "Pricing", id: "pricing" },
  { label: "FAQ", id: "faq" },
];

export type Industry = { icon: LucideIcon; name: string };

export const landingIndustries: Industry[] = [
  { icon: ScissorsIcon, name: "Barber shops" },
  { icon: SparklesIcon, name: "Beauty salons" },
  { icon: DumbbellIcon, name: "Fitness studios" },
  { icon: PersonStandingIcon, name: "Personal trainers" },
  { icon: GraduationCapIcon, name: "Tutors" },
  { icon: BriefcaseIcon, name: "Consultants" },
];

// Every screenshot was exported at the same size, so next/image can be given
// fixed intrinsic dimensions instead of guessing at layout time.
export const showcaseImageSize = { width: 411, height: 871 } as const;

export type ShowcaseRow = {
  eyebrow: string;
  title: string;
  description: string;
  points: string[];
  image: { src: string; alt: string };
};

/**
 * The three jobs the product does, each shown next to the screen that does it.
 * A screenshot beside the claim is worth more than an icon above it.
 */
export const landingShowcase: ShowcaseRow[] = [
  {
    eyebrow: "Online booking",
    title: "Bookings that arrive on their own",
    description:
      "Your booking page lists your services, prices and team, and only offers times that are actually free. Customers book in under a minute, day or night.",
    points: [
      "One link for Instagram, Google or your website",
      "Customers choose the service, the person and the time",
      "Every booking lands straight in your calendar",
    ],
    image: {
      src: "/landing/app-services.png",
      alt: "The service menu in the Manito app, with prices and durations",
    },
  },
  {
    eyebrow: "Team calendar",
    title: "Everyone's day, on one screen",
    description:
      "Each person has their own hours, breaks, days off and services. Changes show up for the whole team straight away, so nobody gets double-booked.",
    points: [
      "Working hours, breaks and days off for each person",
      "Updates live on every phone and computer",
      "A column per person, so gaps are easy to spot",
    ],
    image: {
      src: "/landing/app-calendar.png",
      alt: "A day in the Manito team calendar, one column per team member",
    },
  },
  {
    eyebrow: "Reminders",
    title: "Fewer no-shows",
    description:
      "Customers get a reminder before every visit. Your team hears about new bookings and changes as they happen.",
    points: [
      "You choose when reminders go out",
      "By email, and by text message on Premium",
      "Staff are told about every new booking and change",
    ],
    image: {
      src: "/landing/app-settings.png",
      alt: "Business settings in the Manito app",
    },
  },
];

export type Feature = {
  icon: LucideIcon;
  title: string;
  description: string;
  /** Sold on Premium only (MainApp billing/catalogue.go Features), so the tile says so. */
  premium?: boolean;
};

// Classes are not offered while they are hidden for launch (MANIT-1322), so
// the page does not advertise them anywhere (MANIT-1355).
export const landingFeatures: Feature[] = [
  {
    icon: RepeatIcon,
    title: "Repeat appointments",
    description:
      "Book a regular every week or every month in one go, instead of one visit at a time.",
    premium: true,
  },
  {
    icon: HistoryIcon,
    title: "Activity history",
    description:
      "See who booked, moved or cancelled what, and when, for every change in your business.",
  },
  {
    icon: ContactIcon,
    title: "Customer records",
    description:
      "Contact details, past visits and notes for everyone who has ever booked with you.",
  },
  {
    icon: UserPlusIcon,
    title: "Invite your team",
    description:
      "Add staff with one email, and decide what each person can see and change.",
  },
  {
    icon: PaletteIcon,
    title: "Your brand",
    description:
      "Your name, logo and photos on the booking page your customers see.",
  },
  {
    icon: SmartphoneIcon,
    title: "Mobile app",
    description:
      "Run the day from your phone. Everything stays in step with the web.",
  },
];

export type Step = { title: string; description: string };

export const landingSteps: Step[] = [
  {
    title: "Add your services and team",
    description:
      "Enter what you offer, how long it takes, what it costs, and who does it. Guided setup walks you through it.",
  },
  {
    title: "Share your booking link",
    description:
      "Put it in your Instagram bio, on Google, or send it to regulars. There is nothing for them to install.",
  },
  {
    title: "Take bookings around the clock",
    description:
      "Appointments fill your calendar while you work, and reminders go out without you lifting a finger.",
  },
];

export type PricingPlan = {
  name: string;
  /** Monthly price in euros, formatted at render time. */
  price: number;
  period: string;
  description: string;
  /** Shown above the list, e.g. "Everything in Free, plus". */
  includesLabel?: string;
  features: string[];
  cta: string;
  /** One line under the button: what signing up asks for. */
  ctaNote?: string;
  popular: boolean;
};

/**
 * The Free plan's limits as this page states them, in one place for the card
 * and the FAQ. The page is public and has no plan to read, so these restate
 * the plan MainApp sells (shared/plansnapshot FreeLimits, and the catalogue
 * sync-stripe writes to Stripe); change them together.
 */
const free = { teamMembers: 2, appointmentsPerMonth: 250, emailsPerMonth: 450 };

/**
 * Premium's free trial: once per business, card taken up front, charged on
 * day 15 unless cancelled before (MainApp billing/trial.go, MANIT-1177).
 */
const trialDays = 14;

/**
 * Premium's message credit line. Credit is counted in euros in every currency,
 * so the price section words it differently once another currency is picked.
 */
export const messageCreditFeature = "€4 of message credit a month for each team member";

export const landingPricingPlans: PricingPlan[] = [
  {
    name: "Free",
    price: 0,
    period: "a month",
    description: "For a solo professional or a pair getting started.",
    features: [
      `Up to ${free.teamMembers} team members`,
      `${free.appointmentsPerMonth} appointments a month`,
      "Your own booking page",
      "Your logo and photos on the booking page",
      `Email confirmations and reminders, up to ${free.emailsPerMonth} emails a month`,
      "Customer records",
    ],
    cta: "Start for free",
    ctaNote: "No card needed.",
    popular: false,
  },
  {
    name: "Premium",
    price: 14,
    // Premium is billed for each person on the team, with no ceiling and no
    // seats included (MainApp billing/catalogue.go). Its price is stated
    // before VAT, which Stripe adds for the business's country.
    period: "per team member, a month, excl. VAT",
    description: "For a growing team that wants fewer no-shows.",
    includesLabel: "Everything in Free, plus",
    features: [
      "Any number of team members",
      "Unlimited appointments",
      "Text message reminders",
      messageCreditFeature,
      "Repeat appointments",
    ],
    cta: `Try Premium free for ${trialDays} days`,
    ctaNote: `Card required. Cancel any time before day ${trialDays + 1} and you pay nothing.`,
    popular: true,
  },
];

export type Faq = { question: string; answer: string };

export const landingFaqs: Faq[] = [
  {
    question: "What is Manito?",
    answer:
      "Manito is booking software for businesses that run on appointments. It gives you an online booking page, a shared calendar for your team, and automatic reminders — in one place, on the web and on your phone.",
  },
  {
    question: "How does the Free plan work?",
    answer:
      `Free covers up to ${free.teamMembers} team members and ${free.appointmentsPerMonth} appointments a month, with your own booking page and your logo on it, email confirmations and reminders, and customer records. It includes ${free.emailsPerMonth} emails a month; once they are used, emails pause until the next month starts and Billing tells you so. You don't need a card to sign up, and there is no time limit.`,
  },
  {
    question: "How is Premium billed?",
    answer:
      "€14 a month for each team member, excluding VAT, with no limit on how many you add. Where we charge in your business's currency, Premium is billed in it at a set local price your billing page shows; otherwise it is billed in euros. VAT is added for your country when you subscribe, and businesses with a VAT number can enter it. Each team member also brings €4 of message credit a month, pooled for the whole business, which pays for the emails and text messages Manito sends for you. Anything sent beyond the credit is added to your next invoice. When someone joins or leaves your team, the new count is billed from your next billing date, with no part-month charges or credits in between. If you move back to Free, Premium runs to the end of the month you paid for, and anything sent beyond the credit that month is on your final invoice.",
  },
  {
    question: "Is there a free trial?",
    answer:
      `Yes. Each business can try Premium free for ${trialDays} days, once. You add a card when you start, and nothing is charged during the trial. We email you 3 days before it ends. Cancel any time before day ${trialDays + 1} from your billing page and you pay nothing; otherwise Premium starts on day ${trialDays + 1} and is billed from then on.`,
  },
  {
    question: "Can my whole team use it?",
    answer:
      "Yes. Everyone gets their own calendar, working hours, breaks and services. You invite people by email and decide what each of them can see and change.",
  },
  {
    question: "How do reminders work?",
    answer:
      "Manito sends them for you by email, and by text message on Premium. You choose what triggers a message — a new booking, a change, a cancellation — and how long before an appointment the reminder goes out. Customers and staff have separate settings.",
  },
  {
    question: "Can I change plan later?",
    answer:
      `Whenever you like, with no contracts. Upgrading from Free starts Premium straight away, with the ${trialDays}-day trial if you haven't had it. Moving back to Free, or cancelling, is done from your billing page and takes effect at the end of the period you've paid for. Before you confirm, Billing shows what stays and what stops for your business: your team and every booking already made are kept, while adding team members past Free's limit, repeat bookings going further ahead and text messages stop. Your card, billing email, address and VAT number are changed from the same page.`,
  },
  {
    question: "Is my data safe?",
    answer:
      "Every connection is encrypted, and each business's data is kept apart from every other business's. Only the people you invite can see your customers and your calendar. Manito support opens your business only when you ask for help or to fix a problem, and each visit is recorded.",
  },
  {
    question: "I use another booking tool. Can I switch?",
    answer:
      "Yes. Guided setup gets your services, team and hours in quickly. If you'd like help bringing your existing customers across, get in touch and we'll help.",
  },
];
